import { useState } from 'react'
import type { Appearance } from 'app/lib/forms/appearance'
import { appearanceStyle } from 'app/lib/forms/appearance'
import type { Answer, FormDefinition } from 'app/lib/forms/types'
import { cn } from 'app/lib/utils'
import { logError } from 'lib/logging'
import { FormRenderer } from './FormRenderer'

export type ScreenContent = { title: string; body: string }
export type PacketBranding = { displayName: string; logoUrl?: string; phone?: string }

export type PacketContent = {
  name: string
  welcome?: ScreenContent
  completion?: ScreenContent
  appearance: Appearance
  branding: PacketBranding
  forms: FormDefinition[]
}

type Props = {
  packet: PacketContent
  onSubmitForm: (formId: string, answers: Answer[]) => Promise<void>
  onComplete: () => void
}

type Stage = { kind: `welcome` } | { kind: `form`; index: number } | { kind: `done` }

export function PacketRunner({ packet, onSubmitForm, onComplete }: Props) {
  const [stage, setStage] = useState<Stage>(packet.welcome ? { kind: `welcome` } : { kind: `form`, index: 0 })
  const [failed, setFailed] = useState(false)

  const total = packet.forms.length

  const submitCurrent = async (index: number, answers: Answer[]) => {
    setFailed(false)
    try {
      await onSubmitForm(packet.forms[index].id, answers)
    } catch (error) {
      logError(`paperwork form submission failed`, error)
      setFailed(true)
      return
    }
    if (index + 1 < total) {
      setStage({ kind: `form`, index: index + 1 })
      return
    }
    setStage({ kind: `done` })
    onComplete()
  }

  return (
    <div
      style={appearanceStyle(packet.appearance)}
      className="theme-light @container text-strong min-h-screen bg-[var(--form-bg)] font-[var(--form-font)]"
    >
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 px-4 py-8">
        <header className="flex flex-col gap-1">
          {packet.branding.logoUrl ? (
            <img src={packet.branding.logoUrl} alt={packet.branding.displayName} className="h-10 w-auto self-start" />
          ) : (
            <span className="text-lg font-semibold text-strong">{packet.branding.displayName}</span>
          )}
          {stage.kind === `form` && total > 1 && (
            <nav aria-label="Progress" className="mt-4 flex flex-col gap-2">
              <ol className="flex gap-1.5">
                {packet.forms.map((form, index) => (
                  <li
                    key={form.id}
                    aria-current={index === stage.index ? `step` : undefined}
                    className="flex min-w-0 flex-1 flex-col gap-1.5"
                  >
                    <span
                      className={cn(
                        `h-1.5 rounded-full`,
                        index <= stage.index ? `bg-(--form-accent)` : `bg-surface-strong`,
                      )}
                    />
                    {/* Names need the width a phone does not have; the count below carries it there. Sized
                        off the page, not the window, so the builder's phone preview matches a phone. */}
                    <span
                      className={cn(
                        `hidden truncate text-xs @md:block`,
                        index === stage.index ? `font-medium text-strong` : `text-subtle`,
                      )}
                    >
                      {form.name}
                    </span>
                  </li>
                ))}
              </ol>
              <span className="text-sm text-subtle">{`Form ${stage.index + 1} of ${total}`}</span>
            </nav>
          )}
        </header>

        {stage.kind === `welcome` && packet.welcome && (
          <section className="flex flex-col gap-4">
            <h1 className="text-2xl font-semibold text-strong">{packet.welcome.title}</h1>
            <p className="text-base leading-relaxed text-default">{packet.welcome.body}</p>
            <button
              type="button"
              onClick={() => setStage({ kind: `form`, index: 0 })}
              className="w-full rounded-lg bg-[var(--form-accent)] px-4 py-3 text-base font-medium text-[var(--form-accent-fg)]"
            >
              Get started
            </button>
          </section>
        )}

        {stage.kind === `form` && (
          <section className="flex flex-col gap-4">
            <h1 className="text-xl font-semibold text-strong">{packet.forms[stage.index].name}</h1>
            {failed && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                We could not save that. Check your connection and try again.
              </p>
            )}
            <FormRenderer
              key={packet.forms[stage.index].id}
              form={packet.forms[stage.index]}
              submitLabel={stage.index + 1 === total ? `Finish` : `Continue`}
              onSubmit={(answers) => {
                // submitCurrent swallows its own rejection into the failed banner, so there is
                // nothing left to await or catch here.
                submitCurrent(stage.index, answers)
              }}
            />
          </section>
        )}

        {stage.kind === `done` && (
          <section className="flex flex-col gap-4">
            <h1 className="text-2xl font-semibold text-strong">{packet.completion?.title ?? `All done`}</h1>
            <p className="text-base leading-relaxed text-default">
              {packet.completion?.body ?? `Thank you. Your paperwork has been sent to the office.`}
            </p>
          </section>
        )}

        <footer className="mt-4 flex flex-col gap-1 border-t border-default pt-4 text-sm text-subtle">
          <span>{packet.branding.displayName}</span>
          {packet.branding.phone && <span>{packet.branding.phone}</span>}
        </footer>
      </div>
    </div>
  )
}
